import { getConfiguredGraphQLUrl } from '@repo/env-graphql-url'
import { onlineManager } from '@tanstack/react-query'

/**
 * Grace period before a dropped connection counts. It rides out sleep/wake and
 * Wi-Fi handoff blips so a one-second drop doesn't tear the user out of their work.
 */
export const OFFLINE_CONFIRM_DELAY_MS = 1500

/** How long the connectivity probe waits before giving up. */
export const PROBE_TIMEOUT_MS = 5000

/** Gap between probes while offline, so the app recovers without a focus or `online` event. */
export const OFFLINE_RETRY_MS = 5000

const PRODUCTION_STUDIO_ORIGIN = 'https://studio.creativefabrica.com'
const STAGING_STUDIO_ORIGIN = 'https://studio.staging.creativefabrica.com'

export function hostedStudioOrigin(
  publicEnv: string | undefined = process.env.VITE_PUBLIC_ENV,
): string {
  return publicEnv === 'production' ? PRODUCTION_STUDIO_ORIGIN : STAGING_STUDIO_ORIGIN
}

/** Absolute hosted Studio URL. An address that is already http(s) is left alone. */
export function hostedStudioPageUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  return `${hostedStudioOrigin()}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * URL a connectivity probe should hit. `app://` is served from disk, so a request
 * there succeeds while the machine is still offline. The desktop app probes the
 * API host instead of the Studio site: the staging site sits behind a login wall,
 * so a check against it fails while the network is up. Hosted pages probe their
 * own origin.
 */
export function recoveryProbeUrl(pageUrl: string, graphqlUrl = getConfiguredGraphQLUrl()): string {
  const page = new URL(pageUrl)
  if (page.protocol !== 'app:') {
    return page.origin
  }
  if (graphqlUrl) {
    return new URL(graphqlUrl).origin
  }
  return hostedStudioOrigin()
}

/**
 * A real request is the only trustworthy connectivity signal: around
 * standby/Wi-Fi handoff Chromium can leave `navigator.onLine` stale. `no-cors`
 * keeps a cross-origin check from looking offline when the host omits CORS
 * headers.
 */
export async function probeOnline(url: string): Promise<boolean> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timeout)
  }
}

type Listener = () => void

let installed = false
let online = true
let generation = 0
let pendingProbe: ReturnType<typeof setTimeout> | null = null
let probeInFlight = false
const listeners = new Set<Listener>()

/**
 * Online state of the local `app://` renderer, or null when it isn't installed.
 * Chromium can report `navigator.onLine` as false on `app://` pages even with a
 * network, so going offline takes a failed request to hosted Studio.
 */
export function getAppRendererOnline(): boolean | null {
  return installed ? online : null
}

export function subscribeAppRendererOnline(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function notify() {
  for (const listener of listeners) {
    listener()
  }
}

function applyProbeResult(value: boolean) {
  onlineManager.setOnline(value)
  if (online !== value) {
    online = value
    notify()
  }
}

function runProbe() {
  const probeGeneration = generation
  probeInFlight = true
  void probeOnline(recoveryProbeUrl(window.location.href)).then(result => {
    if (probeGeneration !== generation) {
      return
    }
    probeInFlight = false
    applyProbeResult(result)
    if (!result) {
      scheduleProbe(OFFLINE_RETRY_MS)
    }
  })
}

function scheduleProbe(delayMs: number) {
  if (probeInFlight || pendingProbe !== null) {
    return
  }
  pendingProbe = setTimeout(() => {
    pendingProbe = null
    runProbe()
  }, delayMs)
}

function clearPendingProbe() {
  if (pendingProbe !== null) {
    clearTimeout(pendingProbe)
    pendingProbe = null
  }
}

function probeNow() {
  if (probeInFlight) {
    return
  }
  clearPendingProbe()
  runProbe()
}

/**
 * A request that failed at the network layer is a stronger hint than
 * `navigator.onLine`, which stays true on Wi-Fi without internet. It gets the
 * same grace period as an `offline` event. No-op off `app://`.
 */
export function reportAppRendererNetworkError(): void {
  if (installed && online) {
    scheduleProbe(OFFLINE_CONFIRM_DELAY_MS)
  }
}

function listenToBrowserOnlineEvents(setOnline: (value: boolean) => void) {
  const handleOnline = () => setOnline(true)
  const handleOffline = () => setOnline(false)
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  return () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}

/**
 * Makes a hosted-Studio probe the online signal for `app://` pages, for this
 * store and for TanStack Query. It starts online and only probes at startup when
 * `navigator.onLine` is already false: a first launch needs the network to log
 * in anyway, and a startup probe that loses a race must not strand the window.
 * Later `offline` events and network errors wait {@link OFFLINE_CONFIRM_DELAY_MS}
 * and share one in-flight probe; while offline it re-probes every
 * {@link OFFLINE_RETRY_MS}. No-op on any other protocol, so the web keeps
 * `navigator.onLine`.
 */
export function installAppRendererOnline(): () => void {
  if (installed || window.location.protocol !== 'app:') {
    return () => undefined
  }
  installed = true
  online = true
  onlineManager.setEventListener(() => undefined)
  onlineManager.setOnline(true)

  function handleOffline() {
    if (online) {
      scheduleProbe(OFFLINE_CONFIRM_DELAY_MS)
    }
  }

  function handleRecoverySignal() {
    if (!online) {
      probeNow()
    }
  }

  function handleVisibility() {
    if (document.visibilityState === 'visible') {
      handleRecoverySignal()
    }
  }

  window.addEventListener('offline', handleOffline)
  window.addEventListener('online', handleRecoverySignal)
  window.addEventListener('focus', handleRecoverySignal)
  document.addEventListener('visibilitychange', handleVisibility)
  notify()
  if (!navigator.onLine) {
    runProbe()
  }

  return () => {
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleRecoverySignal)
    window.removeEventListener('focus', handleRecoverySignal)
    document.removeEventListener('visibilitychange', handleVisibility)
    clearPendingProbe()
    generation += 1
    probeInFlight = false
    installed = false
    online = true
    onlineManager.setEventListener(listenToBrowserOnlineEvents)
    onlineManager.setOnline(true)
    notify()
  }
}
